import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ChevronDown, FileText, Filter, Plus } from 'lucide-react';
import { api } from '../services/api.js';
import type { Lieferschein } from '../types';
import { NewLieferscheinModal } from '../components/lieferscheine/NewLieferscheinModal';
import { DocumentDetailSheet } from '../components/documents/DocumentDetailSheet';
import { useLongPressSelect } from '../hooks/useLongPressSelect';
import { SelectionBar } from '../components/ui/SelectionBar';
import { DeleteDocumentDialog } from '../components/ui/DeleteDocumentDialog';
import { SelectableDocumentList } from '../components/documents/SelectableDocumentList';
import { extractApiError } from '../utils/pdfDownload';

const Lieferscheine: React.FC = () => {
  const [lieferscheine, setLieferscheine] = useState<Lieferschein[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [selected, setSelected] = useState<Lieferschein | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const listSelect = useLongPressSelect();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const fetchLieferscheine = async () => {
    try {
      const params = filterStatus ? `?status=${filterStatus}` : '';
      const res = await api.get(`/lieferscheine${params}`);
      setLieferscheine(res.data);
    } catch {
      toast.error('Failed to load delivery notes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchLieferscheine();
  }, [filterStatus]);

  const openDetail = async (id: string) => {
    try {
      const res = await api.get(`/lieferscheine/${id}`);
      setSelected(res.data);
    } catch {
      toast.error('Failed to load details');
    }
  };

  const refreshSelected = async () => {
    await fetchLieferscheine();
    if (!selected) return;
    try {
      const res = await api.get(`/lieferscheine/${selected.id}`);
      setSelected(res.data);
    } catch {
      setSelected(null);
    }
  };

  const deleteSelected = async (deleteOrders: boolean) => {
    const ids = [...listSelect.selected];
    try {
      for (const id of ids) {
        await api.delete(`/lieferscheine/${id}`, {
          params: { delete_orders: deleteOrders },
        });
      }
      toast.success('Deleted');
    } catch (err) {
      toast.error(extractApiError(err).message);
    } finally {
      listSelect.exit();
      if (selected && ids.includes(selected.id)) setSelected(null);
      await fetchLieferscheine();
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-4 md:space-y-6">
      <div className="flex flex-col md:flex-row justify-between md:items-end gap-3">
        <div>
          <h1 className="text-2xl md:text-4xl font-black text-gray-900 tracking-tight flex items-center gap-3">
            <FileText size={28} className="text-brand-500" />
            Delivery notes
          </h1>
          <p className="text-gray-500 font-medium text-sm">
            Own delivery to stores — one document per store and manufacturer
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-brand-500 text-white rounded-xl font-bold text-xs hover:bg-brand-600"
          >
            <Plus size={14} />
            New delivery note
          </button>
          <Filter size={14} className="text-gray-400" />
          <div className="relative">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="appearance-none pl-3 pr-8 py-2.5 rounded-xl font-bold text-xs bg-gray-100 text-gray-600 border-0"
            >
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="finalized">Finalized</option>
              <option value="sent">Sent</option>
            </select>
            <ChevronDown
              size={14}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
          </div>
        </div>
      </div>

      <NewLieferscheinModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(id) => {
          void fetchLieferscheine();
          void openDetail(id);
        }}
      />

      {loading ? (
        <div className="text-center py-20 text-gray-400 font-bold">Loading...</div>
      ) : lieferscheine.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-[2rem] border-2 border-dashed border-gray-300">
          <FileText size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="text-sm font-bold text-gray-600">No delivery notes yet</p>
        </div>
      ) : (
        <div>
          <SelectableDocumentList
            rows={lieferscheine.map((ls) => ({
              id: ls.id,
              title: ls.lieferschein_number,
              subtitle: [ls.pos_name || 'No PoS', ls.manufacturer_name]
                .filter(Boolean)
                .join(' · '),
              status: ls.status,
            }))}
            selecting={listSelect.mode}
            selected={listSelect.selected}
            bindRow={listSelect.bindRow}
            onOpen={(id) => void openDetail(id)}
          />
          {listSelect.mode ? (
            <SelectionBar
              count={listSelect.selected.size}
              onDone={listSelect.exit}
              onDelete={() => setDeleteOpen(true)}
            />
          ) : null}
          <DeleteDocumentDialog
            open={deleteOpen}
            onOpenChange={setDeleteOpen}
            title="Delete delivery notes?"
            message={lieferscheine
              .filter((row) => listSelect.selected.has(row.id))
              .map((row) => row.lieferschein_number)
              .join(', ')}
            onDeleteDocument={() => deleteSelected(false)}
            onDeleteDocumentAndOrders={() => deleteSelected(true)}
          />
        </div>
      )}

      <DocumentDetailSheet
        open={Boolean(selected)}
        kind="lieferschein"
        doc={
          selected
            ? {
                id: selected.id,
                number: selected.lieferschein_number,
                status: selected.status,
                pos_name: selected.pos_name,
                pos_city: selected.pos_city,
                manufacturer_name: selected.manufacturer_name,
                total_net: selected.total_net,
                total_pfand: selected.total_pfand,
                total_gross: selected.total_gross,
                items: selected.items,
                pos_id: selected.pos_id,
                delivery_batch_id: selected.delivery_batch_id,
              }
            : null
        }
        onClose={() => setSelected(null)}
        onRefresh={() => void refreshSelected()}
      />
    </div>
  );
};

export default Lieferscheine;
